// v3/shared/components/Breadcrumbs.tsx — Navigation breadcrumbs
// Uses useLocation() from react-router-dom to build breadcrumb path.
// Zero imports from v1/v2.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../../design-system/utils';

/** Maps path segments to human-readable labels */
const SEGMENT_LABELS: Record<string, string> = {
  v3: 'Home',
  leads: 'Leads',
  applications: 'Applications',
  groups: 'Groups',
  tasks: 'Tasks',
  settings: 'Settings',
  'login-history': 'Login History',
  create: 'Create',
  'residence-verification': 'Residence Verification',
  view: 'View',
};

interface BreadcrumbItem {
  label: string;
  path: string;
}

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const items: BreadcrumbItem[] = [];

  let currentPath = '';
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    // Skip the 'v3' segment — it's represented by the Home icon
    if (segment === 'v3') {
      items.push({ label: 'Home', path: '/' });
      continue;
    }

    const label =
      SEGMENT_LABELS[segment] ??
      // If not in the map, it's likely a dynamic param (ID, phone number, etc.)
      // Try to make it readable
      decodeURIComponent(segment);

    items.push({ label, path: currentPath });
  }

  return items;
}

export function Breadcrumbs() {
  const location = useLocation();
  const items = buildBreadcrumbs(location.pathname);

  if (items.length <= 1) {
    return null; // Don't show breadcrumbs on the root page
  }

  return (
    <nav aria-label="Breadcrumb" className="flex items-center text-sm text-muted-foreground">
      <ol className="flex items-center gap-1.5 flex-wrap">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isHome = index === 0;

          return (
            <li key={item.path} className="flex items-center gap-1.5">
              {index > 0 && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
              )}
              {isLast ? (
                <span
                  className={cn(
                    'font-medium text-foreground',
                    'truncate max-w-[200px]',
                  )}
                  aria-current="page"
                >
                  {isHome ? <Home className="h-4 w-4" /> : item.label}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className={cn(
                    'hover:text-foreground transition-colors',
                    'truncate max-w-[200px]',
                    'min-h-[44px] flex items-center', // touch target
                  )}
                >
                  {isHome ? <Home className="h-4 w-4" /> : item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
